import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, BookOpen, ArrowUpRight, Loader2, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getProducts, getFx, createCheckout, formatFx, validatePromo } from "@/lib/api";

const featureMap = {
  guide_b2c: [
    "10-page consumer guide",
    "A clear, focused transition plan",
    "Instant PDF download",
  ],
  workbook_b2c: [
    "11-page interactive workbook",
    "Hands-on exercises & prompts",
    "Instant PDF download",
  ],
  guide_b2b: [
    "42-page professional guide",
    "Frameworks, roles & resources",
    "Team-ready methodology",
    "Instant PDF download",
  ],
  workbook_b2b: [
    "22-page professional workbook",
    "Templates & progress trackers",
    "Structured programme exercises",
    "Instant PDF download",
  ],
  bundle_b2b: [
    "Includes PivoT ransition-Guide B2B (42 pages)",
    "Includes PivoT ransition-Workbook B2B (22 pages)",
    "Delivered together as a single ZIP",
    "Instant download after payment",
  ],
};

const GROUPS = [
  { key: "B2C", label: "For individuals", tag: "B2C" },
  { key: "B2B", label: "For teams & businesses", tag: "B2B" },
];

const cardAnim = {
  hidden: { opacity: 0, y: 50 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ProductCard = ({ p, index, onBuy, fx }) => {
  const premium = p.tier === "premium";
  const Icon = p.kind === "Workbook" ? BookOpen : FileText;
  return (
    <motion.div
      data-testid={`product-card-${p.id}`}
      custom={index}
      variants={cardAnim}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className={`relative rounded-3xl p-8 lg:p-9 flex flex-col bg-white/5 backdrop-blur-2xl border shadow-[0_8px_40px_rgba(0,0,0,0.4)] ${
        premium ? "border-[var(--neon-cyan)]/40" : "border-white/15"
      }`}
    >
      <div className="flex items-center justify-between mb-7">
        <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
          <Icon size={20} className="text-white" />
        </div>
        <span
          className={`label-caps px-3 py-1 rounded-full ${
            premium ? "bg-[var(--neon-cyan)] text-black" : "bg-white/10 text-white/70"
          }`}
        >
          {p.category} · {p.kind}
        </span>
      </div>

      <h3 className="font-future text-xl lg:text-2xl tracking-tight">{p.name}</h3>
      <p className="mt-3 text-white/60 leading-relaxed text-sm">{p.description}</p>

      <div className="mt-7 flex items-end gap-3">
        <span className="font-future text-3xl lg:text-4xl text-white">SFr {p.price.toFixed(0)}</span>
        <span className="label-caps text-white/40 pb-1.5">{p.pages} pages</span>
      </div>
      {fx && (
        <p className="mt-2 font-mono2 text-[11px] text-white/40" data-testid={`fx-${p.id}`}>
          ≈ {formatFx(p.price, fx).map((f) => f.value).join("  ·  ")}
          <span className="ml-2 text-white/25">daily FX</span>
        </p>
      )}

      <ul className="mt-7 space-y-2.5 flex-1">
        {featureMap[p.id]?.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-white/70">
            <Check size={15} className="text-[var(--neon-cyan)] mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      <button
        data-testid={`buy-${p.id}-btn`}
        onClick={() => onBuy(p)}
        className={`mt-8 label-caps px-6 py-4 rounded-full flex items-center justify-center gap-2 transition-colors duration-300 ${
          premium
            ? "bg-[var(--neon-cyan)] text-black hover:bg-white"
            : "bg-white text-black hover:bg-[var(--neon-cyan)]"
        }`}
      >
        Buy &amp; download <ArrowUpRight size={16} />
      </button>
    </motion.div>
  );
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [fx, setFx] = useState(null);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
    getFx().then(setFx).catch(() => {});
  }, []);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    try {
      const res = await validatePromo(promo.trim());
      if (res.valid) {
        setPromoInfo(res);
        toast.success(`Promo ${res.code} applied — ${res.percent}% off`);
      } else {
        setPromoInfo(null);
        toast.error("That promo code isn't valid");
      }
    } catch {
      toast.error("Could not check the promo code");
    }
  };

  const startCheckout = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const { url } = await createCheckout(selected.id, email, promoInfo ? promoInfo.code : null);
      window.location.href = url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  let cardIndex = 0;

  return (
    <section id="guides" className="relative z-10 overflow-hidden py-28 lg:py-40 bg-[#0D0B14]">
      <div className="bridge-blob w-[520px] h-[520px] bg-[var(--neon-magenta)] -top-20 -left-24" />
      <div className="bridge-blob w-[480px] h-[480px] bg-[var(--volt-blue)] top-40 right-0" />
      <div className="bridge-blob w-[420px] h-[420px] bg-[var(--neon-cyan)] bottom-0 left-1/3 opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16 lg:mb-20">
          <p className="label-caps text-[var(--neon-cyan)] mb-6">The Guides &amp; Workbooks · Choose your crossing</p>
          <h2 className="font-future text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight glow-text">
            Four documents. One destination.
          </h2>
        </div>

        <div className="space-y-16">
          {GROUPS.map((g) => {
            const items = products.filter((p) => p.category === g.key && p.kind !== "Bundle");
            if (!items.length) return null;
            return (
              <div key={g.key} data-testid={`group-${g.key}`}>
                <div className="flex items-center gap-4 mb-8">
                  <span className="label-caps text-white/40">{g.tag}</span>
                  <span className="h-px flex-1 bg-white/10" />
                  <h3 className="font-origin italic font-light text-lg text-white/70">{g.label}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {items.map((p) => (
                    <ProductCard
                      key={p.id}
                      p={p}
                      index={cardIndex++}
                      fx={fx}
                      onBuy={(prod) => {
                        setSelected(prod);
                        setEmail("");
                        setPromo("");
                        setPromoInfo(null);
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {(() => {
            const bundle = products.find((p) => p.kind === "Bundle");
            if (!bundle) return null;
            return (
              <motion.div
                data-testid={`product-card-${bundle.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-3xl p-8 lg:p-12 bg-white/5 backdrop-blur-2xl border border-[var(--neon-cyan)]/50 shadow-[0_8px_50px_rgba(0,240,255,0.12)] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
              >
                <div className="lg:col-span-8">
                  <span className="label-caps px-3 py-1 rounded-full bg-[var(--neon-cyan)] text-black inline-block mb-5">
                    Complete B2B set · Best value
                  </span>
                  <h3 className="font-future text-2xl lg:text-3xl tracking-tight">{bundle.name}</h3>
                  <p className="mt-3 text-white/60 leading-relaxed max-w-2xl">{bundle.description}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                    {featureMap[bundle.id]?.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-white/70">
                        <Check size={15} className="text-[var(--neon-cyan)] mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="lg:col-span-4 lg:text-right">
                  <div className="flex lg:justify-end items-end gap-3">
                    <span className="font-future text-4xl lg:text-5xl text-white">SFr {bundle.price.toFixed(0)}</span>
                    <span className="label-caps text-white/40 pb-2">{bundle.pages} pages</span>
                  </div>
                  {fx && (
                    <p className="mt-2 font-mono2 text-[11px] text-white/40" data-testid={`fx-${bundle.id}`}>
                      ≈ {formatFx(bundle.price, fx).map((f) => f.value).join("  ·  ")}
                    </p>
                  )}
                  <button
                    data-testid={`buy-${bundle.id}-btn`}
                    onClick={() => {
                      setSelected(bundle);
                      setEmail("");
                      setPromo("");
                      setPromoInfo(null);
                    }}
                    className="mt-6 w-full lg:w-auto label-caps px-8 py-4 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 inline-flex items-center justify-center gap-2"
                  >
                    Buy the set <ArrowUpRight size={16} />
                  </button>
                </div>
              </motion.div>
            );
          })()}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && !loading && setSelected(null)}>
        <DialogContent className="bg-[#121118] border-white/10 text-white" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle className="font-future text-lg">{selected?.name}</DialogTitle>
            <DialogDescription className="text-white/60">
              Enter your email — we&apos;ll send your receipt and unlock the download after payment.
              Secure checkout via Stripe.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            <label className="label-caps text-white/50" htmlFor="buyer-email">Email</label>
            <Input
              id="buyer-email"
              data-testid="purchase-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12"
            />

            <label className="label-caps text-white/50 block pt-1" htmlFor="promo-code">Promo code (optional)</label>
            <div className="flex gap-2">
              <Input
                id="promo-code"
                data-testid="promo-input"
                value={promo}
                onChange={(e) => { setPromo(e.target.value); setPromoInfo(null); }}
                placeholder="e.g. WELCOME10"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 uppercase"
              />
              <button
                type="button"
                data-testid="apply-promo-btn"
                onClick={applyPromo}
                className="label-caps px-5 rounded-md border border-white/20 text-white/80 hover:border-white hover:text-white transition-colors duration-300 whitespace-nowrap"
              >
                Apply
              </button>
            </div>

            <div className="flex items-center justify-between pt-1" data-testid="dialog-total">
              <span className="text-white/50 text-sm">Total</span>
              <span className="font-future text-lg text-white">
                {promoInfo ? (
                  <>
                    <span className="text-white/40 line-through text-sm mr-2">SFr {selected?.price.toFixed(2)}</span>
                    SFr {(selected?.price * (1 - promoInfo.percent / 100)).toFixed(2)}
                    <span className="label-caps text-[var(--neon-cyan)] ml-2">-{promoInfo.percent}%</span>
                  </>
                ) : (
                  <>SFr {selected?.price.toFixed(2)}</>
                )}
              </span>
            </div>

            <button
              data-testid="proceed-payment-btn"
              onClick={startCheckout}
              disabled={loading}
              className="w-full label-caps px-6 py-4 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : "Continue to secure payment"}
            </button>
            <p className="font-mono2 text-[11px] text-white/35 text-center pt-1">
              Digital product · Instant download · Prices in SFr
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
